import React, { useEffect, useState } from "react";
import { Intent, FormGroup, Icon, HTMLTable, Button } from "@blueprintjs/core";
import { Popover2InteractionKind, Tooltip2 } from "@blueprintjs/popover2";
import {
  AssistantAIMode,
  AssistantAIModeChangeDetail,
  ASSISTANT_AI_MODE_EVENT,
  ASSISTANT_AI_MODE_STORAGE_KEY,
  getStoredAssistantAIMode
} from "ai-assistant/hooks/useAssistantChat";
import Markdown from "components/Markdown";
import { isTemplateRefreshing } from "components/Template/dependencyIndex";
import { IInputProps } from "components/Template/Input/types";
import { format } from "components/Template/action";
import { doDefault, doInvalid, renderDescription, isLayoutType } from "components/Template/utils";
import { IAction, IModel, IRadio, IResult, ISelect, IUpload } from "components/Template/types";
import { ILayoutProps } from "components/Template/Layout/types";
import { isUndefined } from "lodash";
import RefreshingState from "components/Template/RefreshingState";
import "./Validate.scss";

export interface IValidateProps {
  className?: string;
  showLabel?: boolean;
  showInfo?: boolean;
  intent?: Intent;
  helperText?: string | JSX.Element;
  label?: React.ReactNode;
  labelInfo?: React.ReactNode;
}

export interface IInputValidateProps extends IValidateProps, IInputProps { }

export interface ILayoutValidateProps extends IValidateProps, ILayoutProps { }

export const renderInfo = (description: string | IAction, model: IModel, result?: IResult): JSX.Element | undefined => {
  const processedDescription = renderDescription(description, model, result);
  if (processedDescription) {
    const tooltipContent = (typeof processedDescription === 'string') ?
      <Markdown markdown={processedDescription as string} /> : (
        <HTMLTable striped>
          <thead>
            <tr>
              {(processedDescription as Array<Array<string | number | null>>)[0].map((cell, idx) => <th key={idx}>{cell}</th>)}
            </tr>
          </thead>
          <tbody>
            {(processedDescription as Array<Array<string | number | null>>).slice(1).map((row, idx) => <tr key={idx}>
              {row.map((cell, idx) => <td key={idx}>{cell}</td>)}
            </tr>)}
          </tbody>
        </HTMLTable>
      );

    return <Tooltip2
      content={tooltipContent}
      popoverClassName={"tooltip-content " + (typeof processedDescription === 'string' ? "text-tooltip" : "table-tooltip")}
      openOnTargetFocus={false}
      interactionKind={Popover2InteractionKind.HOVER}
      hoverCloseDelay={200}
      placement="right"
    >
      <Icon className="help-icon" icon={"help"} intent={Intent.PRIMARY} />
    </Tooltip2>
  }
  return undefined;
};

const useAssistantAIModePreference = (): AssistantAIMode => {
  const [assistantMode, setAssistantMode] = useState<AssistantAIMode>(getStoredAssistantAIMode);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleModeChange = (event: Event) => {
      const detail = (event as CustomEvent<AssistantAIModeChangeDetail>).detail;
      if (detail?.aiMode === AssistantAIMode.ASK || detail?.aiMode === AssistantAIMode.EDIT) {
        setAssistantMode(detail.aiMode);
        return;
      }
      setAssistantMode(getStoredAssistantAIMode());
    };

    const handleStorageUpdate = (event: StorageEvent) => {
      if (event.key !== ASSISTANT_AI_MODE_STORAGE_KEY || !event.newValue) {
        return;
      }
      if (event.newValue === AssistantAIMode.ASK || event.newValue === AssistantAIMode.EDIT) {
        setAssistantMode(event.newValue);
      }
    };

    window.addEventListener(ASSISTANT_AI_MODE_EVENT, handleModeChange as EventListener);
    window.addEventListener("storage", handleStorageUpdate);
    return () => {
      window.removeEventListener(ASSISTANT_AI_MODE_EVENT, handleModeChange as EventListener);
      window.removeEventListener("storage", handleStorageUpdate);
    };
  }, []);

  return assistantMode;
};

const SparklesIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M13 3.5 14.6 8.7 19.8 10.3 14.6 11.9 13 17.1 11.4 11.9 6.2 10.3 11.4 8.7 13 3.5Z"
      fill="currentColor"
    />
    <path
      d="M6.5 13.6 7.4 16.5 10.3 17.4 7.4 18.3 6.5 21.2 5.6 18.3 2.7 17.4 5.6 16.5 6.5 13.6Z"
      fill="currentColor"
    />
  </svg>
);

const Validate = (props: React.PropsWithChildren<IInputValidateProps | ILayoutValidateProps>) => {
  const { template, model, result, className, showLabel, showInfo, intent, helperText, label, labelInfo, children } = props;
  const { mutators } = props;

  const invalid = doInvalid(props);
  const errorMessage = invalid && template.validate?.message ? format(model, { sources: template.validate.sources, expression: template.validate.message }) : "";
  const calculated = doDefault(props);
  const refreshing = isTemplateRefreshing(template, props.metadata, mutators.getPath);
  const shouldDimContent = refreshing && (isLayoutType(template.type) || template.type === "select");
  let overrideIntent = Intent.NONE as Intent;
  let overrideMessage = undefined;
  if (helperText && intent) {
    overrideIntent = intent;
    overrideMessage = helperText;
  } else if (!isUndefined(calculated)) {
    let formatted = calculated;
    if (template.type === "select") {
      formatted = (template as ISelect).options?.find((v) => v.value === calculated)?.label || calculated;
    } else if (template.type === "radio") {
      formatted = (template as IRadio).options?.find((v) => v.value === calculated)?.label || calculated;
    } else if (template.type === "file") {
      formatted = (calculated as IUpload).filename;
    }
    overrideMessage = `Default: ${formatted}`;
  }
  const isInput = !isLayoutType(template.type);
  const assistantContextHandler = mutators?.handleAssistantContextTag;
  const assistantModelName = mutators?.assistantContextModelName;
  const assistantPath = isInput && assistantContextHandler ? mutators.getPath(template.name) : undefined;
  const showAssistantAction = Boolean(assistantPath && assistantModelName && assistantContextHandler);
  const assistantMode = useAssistantAIModePreference();
  const isEditMode = assistantMode === AssistantAIMode.EDIT;
  const assistantFieldLabel = typeof template.label === "string" && template.label.trim()
    ? template.label.trim()
    : undefined;
  const assistantTooltipField = assistantFieldLabel ? `“${assistantFieldLabel}”` : "this field";
  const assistantTooltip = isEditMode
    ? `Edit ${assistantTooltipField} with the AI assistant`
    : `Ask the AI assistant about ${assistantTooltipField}`;

  const handleAssistantTagClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (assistantPath && assistantContextHandler) {
      assistantContextHandler(assistantPath, assistantFieldLabel);
    }
  };

  return (
    <FormGroup
      key={template.name}
      className={`${className ?? ""} validate${showAssistantAction ? " validate--assistant" : ""}`}
      intent={invalid ? Intent.DANGER : overrideIntent}
      label={showLabel ? template.label : label}
      helperText={invalid && !isLayoutType(template.type) ? errorMessage : overrideMessage}
      labelFor={template.name}
      labelInfo={showInfo && template.description ? renderInfo(template.description, model, result) : labelInfo}
    >
      {showAssistantAction && (
        <>
          <div className="validate__assistant-bg" />
          <div className="validate__assistant-action">
            <Tooltip2
              content={assistantTooltip}
              hoverCloseDelay={150}
              interactionKind={Popover2InteractionKind.HOVER}
              placement="right"
            >
              <Button
                className="validate__assistant-button"
                icon={isEditMode ? <SparklesIcon /> : "chat"}
                small
                onClick={handleAssistantTagClick}
              >
                {isEditMode ? "EDIT" : "ASK"}
              </Button>
            </Tooltip2>
          </div>
        </>
      )}
      <RefreshingState refreshing={shouldDimContent}>
        <div className="validate__content">
          {invalid && isLayoutType(template.type) ? (
            <p className="fault">
              <Icon icon="error" intent="danger" /> <span>{errorMessage}</span>
            </p>
          ) : (
            ""
          )}
          {children}
        </div>
      </RefreshingState>
    </FormGroup>
  );
};

export default Validate;
